import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TableExportActions from '../common/TableExportActions';
import EstadoAcademicoChip from './EstadoAcademicoChip';
import AlumnoNotasDetalle from './AlumnoNotasDetalle';
import {
  formatFechaHora,
  formatNota,
  formatPromedio,
  getEstadoAcademico,
  getEvaluacionColumnTitle,
  isNotaDesaprobada
} from '../../utils/evaluaciones';
import './Evaluaciones.css';

// Resumen académico del curso: indicadores, matriz alumnos × instancias con promedio y estado de cada alumno,
// y el promedio de cada instancia. Clic en un alumno abre el detalle de sus notas y comentarios.

const ResumenKpi = ({ icon, label, value, tone }) => (
  <article className={`ev-kpi ev-kpi-${tone}`}>
    <i className={`fas ${icon}`} aria-hidden="true" />
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  </article>
);

const CursoResumenAcademico = ({ api, cursoNombre = '' }) => {
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [alumnoDetalle, setAlumnoDetalle] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setResumen(await api.getResumen());
      setError('');
    } catch (err) {
      setError(err?.message || 'No pudimos cargar el resumen académico.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const evaluaciones = useMemo(() => resumen?.evaluaciones || [], [resumen]);
  const alumnos = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = resumen?.alumnos || [];
    return term ? list.filter((alumno) => `${alumno.nombre} ${alumno.email}`.toLowerCase().includes(term)) : list;
  }, [resumen, search]);

  const exportColumns = useMemo(() => [
    { key: 'nombre', title: 'Alumno' },
    ...evaluaciones.map((evaluacion) => ({
      key: `ev-${evaluacion.id}`,
      title: getEvaluacionColumnTitle(evaluacion),
      exportValue: (_, alumno) => {
        const nota = alumno.notas.find((item) => item.idEvaluacion === evaluacion.id);
        return nota?.ausente ? 'Ausente' : formatNota(nota?.nota);
      }
    })),
    { key: 'promedio', title: 'Promedio', exportValue: (value) => formatPromedio(value) },
    { key: 'estado', title: 'Estado', exportValue: (value) => getEstadoAcademico(value).label }
  ], [evaluaciones]);

  if (loading) {
    return <div className="ev-loading"><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Cargando resumen...</div>;
  }

  if (error) {
    return (
      <div className="ev-empty">
        <p className="ev-error-text">{error}</p>
        <button type="button" className="ev-btn ev-btn-secondary" onClick={load}>Reintentar</button>
      </div>
    );
  }

  const totales = resumen?.totales || {};

  return (
    <div className="ev-resumen">
      <section className="ev-kpis">
        <ResumenKpi icon="fa-chart-line" label="Promedio general" value={formatPromedio(totales.promedioGeneral)} tone="primary" />
        <ResumenKpi icon="fa-check-circle" label="Aprobados" value={totales.aprobados} tone="success" />
        <ResumenKpi icon="fa-exclamation-circle" label="En riesgo" value={totales.enRiesgo} tone="danger" />
        <ResumenKpi icon="fa-minus-circle" label="Sin notas" value={totales.sinNotas} tone="muted" />
        <ResumenKpi icon="fa-clipboard-list" label="Evaluaciones" value={totales.totalEvaluaciones} tone="neutral" />
      </section>

      <div className="ev-toolbar">
        <div className="ev-search">
          <i className="fas fa-search" aria-hidden="true" />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar alumno"
            aria-label="Buscar alumno"
          />
        </div>
        <span className="ev-keys-help">Aprueba con 6 · estado según el promedio de las notas</span>
        <div className="ev-toolbar-actions">
          <TableExportActions
            columns={exportColumns}
            rows={alumnos}
            fileName={`resumen-academico-${cursoNombre || 'curso'}`}
            title={`Resumen académico${cursoNombre ? ` - ${cursoNombre}` : ''}`}
            compact
            disabled={!alumnos.length}
          />
          <button type="button" className="ev-btn ev-btn-secondary ev-btn-sm" onClick={load}>
            <i className="fas fa-sync-alt" aria-hidden="true" />
            Actualizar
          </button>
        </div>
      </div>

      {(resumen?.alumnos || []).length === 0 ? (
        <div className="ev-empty">
          <i className="fas fa-user-graduate" aria-hidden="true" />
          <p>El curso no tiene alumnos activos.</p>
        </div>
      ) : (
        <div className="ev-table-wrap ev-matrix-wrap">
          <table className="ev-table ev-matrix">
            <thead>
              <tr>
                <th className="ev-sticky-col">Alumno</th>
                {evaluaciones.map((evaluacion) => (
                  <th key={evaluacion.id} className="ev-matrix-nota" title={formatFechaHora(evaluacion.fechaHora)}>
                    {evaluacion.nombreMateria && <small>{evaluacion.nombreMateria}</small>}
                    {evaluacion.nombre}
                  </th>
                ))}
                <th className="ev-matrix-nota">Promedio</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {alumnos.map((alumno) => {
                const notas = new Map(alumno.notas.map((nota) => [nota.idEvaluacion, nota]));
                return (
                  <tr key={alumno.idAlumno}>
                    <td className="ev-sticky-col">
                      <button type="button" className="ev-link" onClick={() => setAlumnoDetalle(alumno)} title="Ver notas y comentarios">
                        {alumno.nombre}
                      </button>
                    </td>
                    {evaluaciones.map((evaluacion) => {
                      const nota = notas.get(evaluacion.id);
                      const className = nota?.ausente ? 'ausente' : isNotaDesaprobada(nota?.nota) ? 'baja' : '';
                      return (
                        <td key={evaluacion.id} className={`ev-matrix-nota ${className}`} title={nota?.comentario || undefined}>
                          {nota?.ausente ? 'A' : nota?.nota != null ? formatNota(nota.nota) : '—'}
                          {nota?.comentario && <i className="fas fa-comment-dots ev-has-comment" aria-hidden="true" />}
                        </td>
                      );
                    })}
                    <td className={`ev-matrix-nota ev-matrix-promedio ${isNotaDesaprobada(alumno.promedio) ? 'baja' : ''}`}>
                      {formatPromedio(alumno.promedio)}
                    </td>
                    <td>
                      <EstadoAcademicoChip
                        estado={alumno.estado}
                        showPromedio={false}
                        title={alumno.porMateria?.length > 1
                          ? alumno.porMateria.map((m) => `${m.nombreMateria}: ${getEstadoAcademico(m.estado).label} (${formatPromedio(m.promedio)})`).join('\n')
                          : undefined}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {evaluaciones.length > 0 && (
              <tfoot>
                <tr>
                  <td className="ev-sticky-col">Promedio de la instancia</td>
                  {evaluaciones.map((evaluacion) => (
                    <td key={evaluacion.id} className="ev-matrix-nota">{formatPromedio(evaluacion.promedio)}</td>
                  ))}
                  <td className="ev-matrix-nota">{formatPromedio(totales.promedioGeneral)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {alumnoDetalle && (
        <AlumnoNotasDetalle alumno={alumnoDetalle} evaluaciones={evaluaciones} onClose={() => setAlumnoDetalle(null)} />
      )}
    </div>
  );
};

export default CursoResumenAcademico;
